import { parsePhoneNumber } from 'awesome-phonenumber';
import * as Yup from 'yup';
import constants from './constants';
import { method } from 'lodash';

const nameRegExp = new RegExp("^[a-zA-Z-.',]+( [a-zA-Z-.',]+)*$");

const firstLastEmailCharLimit = 50;
const maxCharacters = (label) =>
  `${label} must be at most ${firstLastEmailCharLimit} characters.`;

const firstNameSchema = Yup.string()
  .trim()
  .required('First name is required.')
  .max(firstLastEmailCharLimit, maxCharacters('First name'))
  .matches(nameRegExp, 'Please enter a valid first name.');

const lastNameSchema = Yup.string()
  .trim()
  .required('Last name is required.')
  .max(firstLastEmailCharLimit, maxCharacters('Last name'))
  .matches(nameRegExp, 'Please enter a valid last name.');

const emailSchema = Yup.string()
  .trim()
  .email('Please input a valid email.')
  .max(firstLastEmailCharLimit, maxCharacters('Email'))
  .required('Email is required.');

const isValidPhoneNumber = (value, { mobileOnly } = {}) => {
  if (!value) return true; // let .required() handle empty state
  // reject upfront: the library maps letters to keypad digits (vanity
  // numbers, e.g. "abc" -> "222"), which isn't a valid phone entry here
  if (!/^\+?[0-9\s\-()]+$/.test(value)) return false;
  // default to PH so a bare local number still validates,
  // while a number entered with its own "+" country code is checked as-is
  const parsed = parsePhoneNumber(value, { regionCode: 'PH' });
  return parsed.valid && (!mobileOnly || parsed.typeIsMobile);
};

const getPhoneSchema = (label) =>
  Yup.string()
    .trim()
    .required(`${label} is required.`)
    .test('is-valid-phone', `Invalid ${label}.`, (value) =>
      isValidPhoneNumber(value, { mobileOnly: true }),
    );

const phoneSchema = getPhoneSchema('Phone number');
const contactSchema = getPhoneSchema('Contact number');

const getInternationalPhoneSchema = (label) =>
  Yup.string()
    .trim()
    .required(`${label} is required.`)
    .test('is-valid-phone', `Invalid ${label}.`, (value) =>
      isValidPhoneNumber(value),
    );

const internationalPhoneSchema = getInternationalPhoneSchema('Phone number');

const passwordSchema = Yup.string()
  .required('Password is required.')
  .min(8)
  .label('Password');

const confirmPasswordSchema = Yup.string()
  .required()
  .min(8)
  .label('Confirm Password');

const oldPasswordSchema = confirmPasswordSchema.label('Old Password');

const newPasswordSchema = confirmPasswordSchema.label('New Password');

const confirmNewPasswordSchema = confirmPasswordSchema.oneOf(
  [Yup.ref('newPassword'), null],
  'Please make sure your passwords match.',
);

//#region card schema

const cardNumberSchema = Yup.number()
  .typeError('Card Number must be a number.')
  .required()
  .label('Card Number');

const monthSchema = Yup.number()
  .typeError('Month must be a number.')
  .required()
  .label('Month');
const yearSchema = Yup.number()
  .typeError('Year must be a number.')
  .required()
  .label('Year');

const cvvSchema = Yup.number()
  .typeError('CVV must be a number.')
  .required()
  .label('CVV');
//#endregion

const paymentSchema = Yup.object().shape({
  method: fieldRequired('Payment Method'),
  cardNumber: Yup.number().when('method', {
    is: 'card',
    then: cardNumberSchema,
  }),
  cardMonth: Yup.number().when('method', { is: 'card', then: monthSchema }),
  cardYear: Yup.number().when('method', { is: 'card', then: yearSchema }),
  cardCvv: Yup.number().when('method', { is: 'card', then: cvvSchema }),
});

const distanceSchema = Yup.number()
  .typeError('Distance must be a number.')
  .integer('Distance must be a whole number.')
  .min(
    constants.DEFAULT_DISTANCE,
    `Minimum of ${constants.DEFAULT_DISTANCE} ${constants.DISTANCE_UNIT}`,
  )
  .max(
    constants.DISTANCE_MAX,
    `More than ${constants.DISTANCE_MAX} ${constants.DISTANCE_UNIT} is not allowed.`,
  )
  .required()
  .label('Distance');

const zipSchema = Yup.string().required().label('Zip Code');

function checkedSchema(label) {
  return Yup.boolean().required().oneOf([true], label);
}

function conditionalRequired(label, type, toField, equalToValue) {
  return Yup[type]().when(toField, {
    is: (toField) => toField === equalToValue,
    then: fieldRequired(label, type),
    otherwise: Yup[type](),
  });
}

function createSchema(object) {
  return Yup.object().shape(object);
}

function fieldRequired(label, type = 'string') {
  return Yup[type]().required().nullable().label(label);
}

function typeSchema(label, type) {
  return Yup[type]().label(label);
}

export default {
  firstLastEmailCharLimit,
  firstNameSchema,
  lastNameSchema,
  emailSchema,
  phoneSchema,
  contactSchema,
  internationalPhoneSchema,
  passwordSchema,
  confirmPasswordSchema,
  oldPasswordSchema,
  newPasswordSchema,
  confirmNewPasswordSchema,
  cardNumberSchema,
  paymentSchema,
  monthSchema,
  yearSchema,
  cvvSchema,
  zipSchema,
  distanceSchema,
  checkedSchema,
  conditionalRequired,
  createSchema,
  fieldRequired,
  typeSchema,
};
